import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { closeTopForm } from '../utils/formStack';

// Keyboard Escape = "one step back" everywhere (desktop counterpart of the Android
// hardware back button in BackButtonHandler.jsx):
//   1) an open add/edit form / modal → close it (content is NOT cleared);
//   2) we navigated INTO this page inside the app (nav click, dashboard tile,
//      row drill-in) → navigate back one history step;
//   3) nothing to go back to (login, first page after sign-in) → nothing.
// Depth is counted from the router's own PUSH/POP events rather than a fixed list
// of "root" paths, so a page like /app/members is a root when a plain user lands
// on it but goes back when reached via Functions → row click.
// Ignored while a native <select> is focused (Escape there just closes the list)
// and when a component already handled the key.
export default function EscapeHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();
  const depth = useRef(0);

  // Track how many in-app steps we can pop. Login/logout use replace, so they
  // never count; a POP (browser back or our own navigate(-1)) unwinds one.
  useEffect(() => {
    if (navType === 'PUSH') depth.current += 1;
    else if (navType === 'POP') depth.current = Math.max(0, depth.current - 1);
  }, [location.key, navType]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'SELECT') return;
      if (closeTopForm()) { e.preventDefault(); return; }
      if (depth.current <= 0) return;
      // A filled input keeps its text; Esc simply leaves the page.
      if (tag === 'INPUT' || tag === 'TEXTAREA') document.activeElement.blur();
      e.preventDefault();
      navigate(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  return null;
}
